<template>
  <div
    :class="['app-container', isDark ? 'dark-theme' : 'light-theme', { 'blurred-content': showSignupGateDialog && !isBondDetailsPage }, { 'full-bleed-nav': route.path === '/refer' }, { 'app-container--transparent': isDiwaliActive }]">
    <navbar :navbar-opacity="navbarOpacity" />
    <main>
      <slot />
    </main>

    <footerComponent :style="{ backgroundColor: colors.secondaryBg }" />

    <!-- Signup Gate Dialog - Only on non-bond-details pages (bond details uses BondSignupGateModal) -->
    <ClientOnly>
      <SignupGateDialog v-if="isBondDetailsPage === false" v-model="showSignupGateDialog" />
    </ClientOnly>
    <!-- Global search modal (Algolia-style): bonds + FDs, click bond → detail page, click FD → invest platform -->
    <ClientOnly>
      <GlobalSearchModal v-model="globalSearchOpen" />
    </ClientOnly>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from "vue";
import { useRoute } from "vue-router";
import { useRuntimeConfig } from "#app";
import navbar from "@/components/navbar.vue";
import footerComponent from "~/components/footer.vue";
import GlobalSearchModal from "~/components/GlobalSearchModal.vue";
import { useTheme } from "~/composables/useTheme";
import { useSignupGate } from "~/composables/useSignupGate";
import SignupGateDialog from "~/components/common/SignupGateDialog.vue";

const route = useRoute();
const config = useRuntimeConfig();
const isDiwaliActive = computed(() => config.public.VITE_ENABLE_DIWALI_THEME === "true");
const globalSearchOpen = useState("global-search-open", () => false);
const { colors, themeName } = useTheme();
const isDark = computed(() => themeName.value === "dark");

// Check if current route is a bond details page
const isBondDetailsPage = computed(() => {
  return route.path.startsWith('/products/bonds/') && route.path !== '/products/bonds'
});

// Initialize signup gate (only tracks on bond details pages)
const { showDialog: showSignupGateDialog, initGate } = useSignupGate();

const navbarOpacity = ref(1);
let lastScrollY = 0;

// const handleScroll = () => {
//   if (typeof window === "undefined") return; // SSR safety

//   const currentScrollY = window.scrollY;
//   const scrollDirectionDown = currentScrollY > lastScrollY;
//   const windowHeight = window.innerHeight;
//   const documentHeight = document.body.scrollHeight;
//   const distanceFromBottom = documentHeight - (currentScrollY + windowHeight);

//   // Fade out near bottom when scrolling down
//   if (scrollDirectionDown && distanceFromBottom < 10000) {
//     navbarOpacity.value = 0;
//   } else {
//     navbarOpacity.value = 1;
//   }

//   lastScrollY = currentScrollY;
// };

// onMounted(() => {
//   if (typeof window !== "undefined") {
//     lastScrollY = window.scrollY;
//     window.addEventListener("scroll", handleScroll);
//   }
// });

// onBeforeUnmount(() => {
//   if (typeof window !== "undefined") {
//     window.removeEventListener("scroll", handleScroll);
//   }
// });

// Signup gate disabled for bond details - useBondSignupGate handles it in [id].vue
watch(
  () => route.path,
  (newPath) => {
    const isBondPage = newPath.startsWith('/products/bonds/') && newPath !== '/products/bonds';
    if (isBondPage) {
      // Skip - bond details uses useBondSignupGate instead
    } else {
      initGate();
    }
  },
  { immediate: true }
);

onMounted(() => {
  if (isBondDetailsPage.value) {
    // Skip - bond details uses useBondSignupGate
  } else {
    initGate();
  }
});
</script>

<style scoped>
.app-container--transparent.light-theme {
  background-color: transparent !important;
}

.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

main {
  flex: 1;
}

/* Theme classes */
.light-theme {
  background-color: #fff;
  color: #000;
}

.dark-theme {
  background-color: #121212;
  color: #fff;
}

/* Navbar styles */
.app-container > :deep(nav) {
  position: sticky;
  top: 20px;
  z-index: 3000;
}

.app-container.full-bleed-nav > :deep(nav) {
  position: sticky;
  width: 100%;
  z-index: 3000;
  margin-bottom: -70px;
}

/* Blur content when signup gate dialog is shown */
.blurred-content {
  filter: blur(4px);
  opacity: 0.6;
  pointer-events: none;
  user-select: none;
  transition: filter 0.3s ease, opacity 0.3s ease;
}

.blurred-content main,
.blurred-content > :deep(nav),
.blurred-content footer {
  filter: blur(4px);
  pointer-events: none;
}
</style>
